import { type GetUsersResponse } from "@mg/schemas/src/christo/catalyst";
import { MagnifyingGlass, ArrowCounterClockwise } from "@phosphor-icons/react";
import {
  Button,
  Dialog,
  Flex,
  Heading,
  Table,
  Text,
  TextField,
  Tooltip,
} from "@radix-ui/themes";
// eslint-disable-next-line import/named
import { createRoute, redirect } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import { AvatarWithInitials } from "../../../components/AvatarWithInitials";
import { useUI } from "../../../contexts/ui";
import { errorAnalyticsPayload, useAnalytics } from "../../../utils/analytics";
import { requiresAuth, canManageUsers } from "../../../utils/auth";
import { errorMessage } from "../../../utils/http";
import {
  useGetArchivedUsers,
  useUnarchiveUser,
} from "../../../utils/queries/users";
import { authLayoutRoute } from "../../auth-layout/route";
import { ticketsRoute } from "../../tickets/route";

export const archivedUsersRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "archived-users",
  beforeLoad() {
    const canAccess = canManageUsers();

    if (!canAccess) {
      throw redirect({
        to: ticketsRoute.to,
      } as any);
    }

    requiresAuth();
  },
  component: ArchivedUsers,
});

function ArchivedUsers() {
  const posthog = useAnalytics("ArchivedUsers");
  const { notify } = useUI();

  const [filters, setFilters] = useState<{ name: string }>({ name: "" });
  const [showUnarchiveDialog, setShowUnarchiveDialog] = useState(false);
  const [unarchivedUserData, setUnarchivedUserData] = useState<
    GetUsersResponse[number] | null
  >(null);

  const {
    data: unsortedArchivedUsers,
    refetch,
    isPending,
    isRefetching,
  } = useGetArchivedUsers();

  // Filter archived users based on search
  const archivedUsers = useMemo(() => {
    if (!unsortedArchivedUsers) return [];

    return unsortedArchivedUsers
      .filter((user) => {
        if (!user.email) return false;

        // Apply name/email filter if provided
        if (filters.name) {
          return (
            user.name?.toLowerCase().includes(filters.name.toLowerCase()) ||
            user.email?.toLowerCase().includes(filters.name.toLowerCase())
          );
        }

        return true;
      })
      .toSorted((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
  }, [unsortedArchivedUsers, filters.name]);

  const unarchiveUserMutation = useUnarchiveUser();

  const isLoading =
    isPending || isRefetching || unarchiveUserMutation.isPending;

  const handleUnarchiveUser = async (user: GetUsersResponse[number]) => {
    const startTime = performance.now();

    try {
      await unarchiveUserMutation.mutateAsync(user._id);

      // Store the user data before refetching (since they won't be in archived list after)
      // The backend will restore the original email from archivedEmail
      setUnarchivedUserData(user);
      setShowUnarchiveDialog(true);

      // Refetch to update the list
      await refetch();

      posthog.capture("user_unarchived", {
        userId: user._id,
        userEmail: user.email,
        userName: user.name,
        userRole: user.role,
        durationSeconds: (performance.now() - startTime) / 1000,
      });

      notify({
        variant: "success",
        title: `${user.name} successfully unarchived`,
        message: "User has been restored and can now access the system.",
        leadingIcon: <ArrowCounterClockwise color="rgb(var(--base-black))" />,
      });
    } catch (error) {
      const errorPayload = errorAnalyticsPayload(error);
      posthog.capture("user_unarchive_failed", errorPayload);

      notify({
        variant: "error",
        title: "Failed to unarchive user",
        message: await errorMessage(error),
      });
    }
  };

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center p-8"
        data-testid="archived-users-loading"
      >
        <Text>Loading archived users...</Text>
      </div>
    );
  }

  return (
    <div className="mb-4 overflow-auto px-8 py-2">
      <Flex
        direction="row"
        align="center"
        justify="between"
        className="mb-2 px-2 py-4"
      >
        <Heading
          size="8"
          className="font-sans"
          data-testid="archived-users-heading"
        >
          Archived Users
          {archivedUsers?.length
            ? ` (${archivedUsers.length.toLocaleString()} users)`
            : ""}
        </Heading>
        <Flex align="center" gap="2">
          <TextField.Root
            placeholder="Search Archived Users"
            size="1"
            value={filters.name}
            onChange={(e) => setFilters({ name: e.target.value })}
            data-testid="archived-users-search"
          >
            <TextField.Slot>
              <MagnifyingGlass />
            </TextField.Slot>
          </TextField.Root>
        </Flex>
      </Flex>

      <Flex direction="column" className="px-2">
        <Text size="3" color="gray">
          This page shows all users who have been archived from your enterprise.
          Archived users cannot access the system and their accounts are
          deactivated.
          <br />
          <br />
          You can restore archived users by unarchiving them, which will
          reactivate their account and restore their original email address.
          When unarchiving, the user&apos;s email will be changed back to their
          original email (stored in the &quot;Original Email&quot; column) and
          they will regain access to the platform.
        </Text>
      </Flex>

      <div className="px-2 py-4">
        {archivedUsers.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center p-8 text-center"
            data-testid="archived-users-empty-state"
          >
            <Text size="3" className="text-gray-600 mb-2">
              {filters.name
                ? "No archived users found matching your search."
                : "No archived users found."}
            </Text>
            {filters.name && (
              <Button
                variant="outline"
                onClick={() => setFilters({ name: "" })}
                data-testid="clear-search"
              >
                Clear search
              </Button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg border">
            <Table.Root
              variant="surface"
              className="w-full min-w-[300px]"
              data-testid="archived-users-table"
            >
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeaderCell data-testid="archived-column-header-name">
                    Name
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell data-testid="archived-column-header-current-email">
                    Current Email
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell data-testid="archived-column-header-original-email">
                    Original Email
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell data-testid="archived-column-header-enterprise">
                    Enterprise
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell data-testid="archived-column-header-role">
                    Role
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell
                    className="w-[150px]"
                    data-testid="archived-column-header-actions"
                  >
                    Actions
                  </Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {archivedUsers.map((user: GetUsersResponse[number]) => (
                  <Table.Row
                    key={user._id}
                    className="bg-[#F9F9F9] hover:bg-carbon-50"
                  >
                    <Table.Cell className="align-middle">
                      <AvatarWithInitials
                        avatar={user?.avatar}
                        name={user?.name}
                        size={8}
                        square
                      />
                      <Text weight="bold" className="pl-2">
                        {user.name || "Unnamed User"}
                      </Text>
                    </Table.Cell>
                    <Table.Cell className="align-middle">
                      <Text size="2" className="text-gray-600">
                        {user.email}
                      </Text>
                    </Table.Cell>
                    <Table.Cell className="align-middle">
                      <Text size="2" className="text-gray-600">
                        {user.archivedEmail || "Unknown"}
                      </Text>
                    </Table.Cell>
                    <Table.Cell className="align-middle">
                      <Text size="2" className="text-gray-600">
                        {user.enterprise && typeof user.enterprise === "object"
                          ? user.enterprise.name
                          : user.enterpriseProfiles
                              ?.map((profile) => profile.enterprise)
                              .join(", ") || "Unknown"}
                      </Text>
                    </Table.Cell>
                    <Table.Cell className="align-middle capitalize">
                      <Text size="2" className="capitalize">
                        {user.role?.replace(/_/g, " ").toLowerCase() ||
                          "Unknown"}
                      </Text>
                    </Table.Cell>
                    <Table.Cell className="align-middle">
                      <Tooltip content="Restore user access">
                        <Button
                          variant="outline"
                          size="1"
                          onClick={() => handleUnarchiveUser(user)}
                          disabled={unarchiveUserMutation.isPending}
                          data-testid={`unarchive-user-${user._id}`}
                        >
                          <ArrowCounterClockwise size={14} />
                          Unarchive
                        </Button>
                      </Tooltip>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </div>
        )}
      </div>

      {/* Unarchive Confirmation Dialog */}
      <Dialog.Root
        open={showUnarchiveDialog}
        onOpenChange={setShowUnarchiveDialog}
      >
        <Dialog.Content className="w-[90vw] max-w-2xl">
          <Dialog.Title>User Successfully Unarchived</Dialog.Title>
          <div className="mt-4 space-y-4">
            <Text size="2" className="text-gray-600">
              The user has been successfully restored. Here is their updated
              user data:
            </Text>

            {unarchivedUserData && (
              <div className="bg-gray-50 space-y-2 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <Text weight="medium">Name:</Text>
                    <Text>{unarchivedUserData.name || "Unnamed User"}</Text>
                  </div>
                  <div>
                    <Text weight="medium">Original Email:</Text>
                    <Text>{unarchivedUserData.archivedEmail || "Unknown"}</Text>
                  </div>
                  <div>
                    <Text weight="medium">Enterprise:</Text>
                    <Text>
                      {unarchivedUserData.enterprise &&
                      typeof unarchivedUserData.enterprise === "object"
                        ? unarchivedUserData.enterprise.name
                        : unarchivedUserData.enterpriseProfiles
                            ?.map((profile) => profile.enterprise)
                            .join(", ") || "Unknown"}
                    </Text>
                  </div>
                  <div>
                    <Text weight="medium">Role:</Text>
                    <Text className="capitalize">
                      {unarchivedUserData.role
                        ?.replace(/_/g, " ")
                        .toLowerCase() || "Unknown"}
                    </Text>
                  </div>
                  <div>
                    <Text weight="medium">User ID:</Text>
                    <Text className="font-mono text-xs">
                      {unarchivedUserData._id}
                    </Text>
                  </div>
                </div>

                <div className="bg-green-50 border-green-200 mt-4 rounded border p-3">
                  <Text size="2" className="text-green-800">
                    ✓ <strong>Verification:</strong> User data has been verified
                    to ensure:
                  </Text>
                  <ul className="text-green-700 ml-4 mt-2 space-y-1 text-sm">
                    <li>
                      • <code>archivedAt</code> property has been completely
                      removed
                    </li>
                    <li>
                      • Email has been restored to the original address (stored
                      in <code>archivedEmail</code>)
                    </li>
                    <li>• User can now access the system normally</li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray">
                Close
              </Button>
            </Dialog.Close>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </div>
  );
}
